/**
 * Anam SDK event wiring and pub/sub for derived signals the widget cares
 * about: finalized user transcript, speaking state, persona speech stream
 * text (word-by-word captions), and active input audio MediaStream.
 */
import type AnamClient from '@anam-ai/js-sdk/dist/module/AnamClient';
import { AnamEvent, type Message } from '@anam-ai/js-sdk';

let speaking = false;
let lastUserTurn = '';
let personaSpeechText = '';
let activePersonaMessageId = '';
let inputAudioStream: MediaStream | null = null;
const speakingListeners = new Set<(speaking: boolean) => void>();
const transcriptListeners = new Set<(text: string) => void>();
const connectionClosedListeners = new Set<() => void>();
const personaSpeechListeners = new Set<(text: string) => void>();
const inputAudioStreamListeners = new Set<(stream: MediaStream | null) => void>();

export function setSpeaking(next: boolean): void {
  if (speaking === next) return;
  speaking = next;
  for (const l of speakingListeners) l(next);
}

export function onSpeakingChange(cb: (speaking: boolean) => void): () => void {
  speakingListeners.add(cb);
  return () => {
    speakingListeners.delete(cb);
  };
}

export function onUserTranscript(cb: (text: string) => void): () => void {
  transcriptListeners.add(cb);
  return () => {
    transcriptListeners.delete(cb);
  };
}

export function onConnectionClosed(cb: () => void): () => void {
  connectionClosedListeners.add(cb);
  return () => {
    connectionClosedListeners.delete(cb);
  };
}

export function clearPersonaSpeechText(): void {
  activePersonaMessageId = '';
  if (!personaSpeechText) return;
  personaSpeechText = '';
  for (const cb of personaSpeechListeners) cb('');
}

export function onPersonaSpeechText(cb: (text: string) => void): () => void {
  personaSpeechListeners.add(cb);
  return () => {
    personaSpeechListeners.delete(cb);
  };
}

export function getPersonaSpeechText(): string {
  return personaSpeechText;
}

function appendPersonaSpeechChunk(current: string, incoming: string): string {
  if (!current) return incoming;
  if (!incoming) return current;
  if (/\s$/.test(current) || /^[\s.,!?;:)'"\]]/.test(incoming)) {
    return current + incoming;
  }
  if (/[a-z0-9]$/.test(current) && /^[A-Z]/.test(incoming)) {
    return `${current}. ${incoming}`;
  }
  return `${current} ${incoming}`;
}

export function onInputAudioStream(cb: (stream: MediaStream | null) => void): () => void {
  inputAudioStreamListeners.add(cb);
  return () => {
    inputAudioStreamListeners.delete(cb);
  };
}

export function getInputAudioStream(): MediaStream | null {
  return inputAudioStream;
}

export function attachListeners(c: AnamClient): void {
  c.addListener(AnamEvent.MESSAGE_HISTORY_UPDATED, (messages: Message[]) => {
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    if (!lastUser || lastUser.content === lastUserTurn) return;
    lastUserTurn = lastUser.content;
    for (const cb of transcriptListeners) cb(lastUser.content);
  });
  c.addListener(AnamEvent.MESSAGE_STREAM_EVENT_RECEIVED, (e) => {
    if (e.role !== 'persona') return;
    if (e.id && e.id !== activePersonaMessageId) {
      activePersonaMessageId = e.id;
      personaSpeechText = '';
    }
    if (e.content) {
      personaSpeechText = appendPersonaSpeechChunk(personaSpeechText, e.content);
      for (const cb of personaSpeechListeners) cb(personaSpeechText);
    }
    setSpeaking(!(e.endOfSpeech || e.interrupted));
  });
  c.addListener(AnamEvent.INPUT_AUDIO_STREAM_STARTED, (stream: MediaStream) => {
    inputAudioStream = stream;
    for (const cb of inputAudioStreamListeners) cb(stream);
  });
  c.addListener(AnamEvent.TALK_STREAM_INTERRUPTED, () => setSpeaking(false));
  c.addListener(AnamEvent.CONNECTION_CLOSED, () => {
    setSpeaking(false);
    for (const cb of connectionClosedListeners) cb();
  });
}

/** Reset ephemeral SDK-derived state between sessions without dropping React listeners. */
export function resetEvents(): void {
  setSpeaking(false);
  lastUserTurn = '';
  clearPersonaSpeechText();
  if (inputAudioStream !== null) {
    inputAudioStream = null;
    for (const cb of inputAudioStreamListeners) cb(null);
  }
}

export function clearListeners(): void {
  speakingListeners.clear();
  transcriptListeners.clear();
  connectionClosedListeners.clear();
  personaSpeechListeners.clear();
  inputAudioStreamListeners.clear();
}

export function isSpeaking(): boolean {
  return speaking;
}
